import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useRemoveWorkspaceMember,
  useUpdateWorkspaceMemberRole,
} from '../../hooks/useWorkspaces'
import { useAuthStore } from '../../store/auth.store'
import { WorkspaceMember, WorkspaceRole } from '../../types/models'
import { formatDate } from '../../utils/formatDate'
import { Avatar } from '../common/Avatar'
import { Button } from '../common/Button'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { Dropdown } from '../common/Dropdown'
import { AddMemberModal } from './AddMemberModal'
import './WorkspaceMemberManager.css'

export interface WorkspaceMemberManagerProps {
  workspaceId: number
  members: WorkspaceMember[]
  currentUserRole?: WorkspaceRole
}

/**
 * WorkspaceMemberManager Component
 * Manages workspace members (add, remove, update role)
 * Implements Requirements 3.1, 3.2, 3.3, 3.4
 */
export const WorkspaceMemberManager: React.FC<WorkspaceMemberManagerProps> = ({
  workspaceId,
  members,
  currentUserRole,
}) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<WorkspaceMember | null>(
    null
  )

  const removeMember = useRemoveWorkspaceMember(workspaceId)
  const updateMemberRole = useUpdateWorkspaceMemberRole(workspaceId)

  const canManageMembers =
    currentUserRole === WorkspaceRole.OWNER ||
    currentUserRole === WorkspaceRole.ADMIN

  const roleOptions = [
    { value: WorkspaceRole.OWNER, label: t('workspace.roleOwner') },
    { value: WorkspaceRole.ADMIN, label: t('workspace.roleAdmin') },
    { value: WorkspaceRole.MEMBER, label: t('workspace.roleMember') },
  ]

  const handleRoleChange = async (memberId: number, newRole: WorkspaceRole) => {
    try {
      await updateMemberRole.mutateAsync({
        memberId,
        data: { role: newRole },
      })
    } catch (error) {
      // Error is handled by the mutation hook
    }
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove) return

    try {
      await removeMember.mutateAsync(memberToRemove.userId)
      setMemberToRemove(null)
    } catch (error) {
      // Error is handled by the mutation hook
    }
  }

  return (
    <div className="workspace-member-manager">
      {canManageMembers && (
        <div className="workspace-member-manager__header">
          <Button onClick={() => setIsAddModalOpen(true)}>
            {t('workspace.addMember')}
          </Button>
        </div>
      )}

      {!canManageMembers && (
        <p className="workspace-member-manager__no-permission">
          {t('workspace.noPermissionManage')}
        </p>
      )}

      <div className="workspace-member-manager__list">
        {members.map((member) => {
          const isCurrentUser = member.userId === user?.id
          const canModify = canManageMembers && !isCurrentUser

          return (
            <div key={member.userId} className="workspace-member-manager__item">
              <div className="workspace-member-manager__info">
                <Avatar
                  user={{
                    name: member.userName,
                    email: member.userEmail,
                  }}
                  size="md"
                />
                <div className="workspace-member-manager__details">
                  <div className="workspace-member-manager__name">
                    {member.userName}
                    {isCurrentUser && (
                      <span className="workspace-member-manager__you-badge">
                        {t('workspace.youBadge')}
                      </span>
                    )}
                  </div>
                  <div className="workspace-member-manager__email">
                    {member.userEmail}
                  </div>
                  <div className="workspace-member-manager__joined">
                    {t('workspace.joined', {
                      date: formatDate(member.joinedAt),
                    })}
                  </div>
                </div>
              </div>

              <div className="workspace-member-manager__actions">
                {canModify ? (
                  <>
                    <Dropdown
                      options={roleOptions}
                      value={member.role}
                      onChange={(value) =>
                        handleRoleChange(member.userId, value as WorkspaceRole)
                      }
                      placeholder={t('workspace.selectRole')}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setMemberToRemove(member)}
                    >
                      {t('common.remove')}
                    </Button>
                  </>
                ) : (
                  <span className="workspace-member-manager__role-badge">
                    {member.role}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <AddMemberModal
        workspaceId={workspaceId}
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      <ConfirmDialog
        isOpen={!!memberToRemove}
        title={t('workspace.removeMember')}
        message={t('workspace.removeMemberConfirm', {
          name: memberToRemove?.userName,
        })}
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
        confirmVariant="danger"
        onConfirm={handleRemoveMember}
        onCancel={() => setMemberToRemove(null)}
        loading={removeMember.isPending}
      />
    </div>
  )
}
